import { createTheme } from '@mui/material';

export const APP_APPEARANCE_STORAGE_KEY = 'mail-aid:appAppearance.v1';

/**
 * @typedef {'curriculum'|'pr-gipsy'|'pr-cherno'|'pr-kaiju'|'pr-shatterdome'|'sw-rebel'|'sw-empire'|'sw-jedi'|'sw-sith'|'sw-mandalorian'|'st-tng'|'st-tos'|'st-ds9'|'st-voyager'|'st-discovery'|'nav-honeycomb'|'nav-honeycomb-dusk'|'nav-honeycomb-light'} AppAppearanceId
 */
/**
 * @typedef {'curriculum'|'starwars'|'startrek'|'pacificrim'|'honeycomb'} AppearanceFamily
 */

export const APPEARANCE_FAMILY_OPTIONS = [
  { id: 'curriculum', labelKey: 'appearanceStandard', defaultAppearance: 'curriculum' },
  { id: 'starwars', labelKey: 'appearanceStarWars', defaultAppearance: 'sw-rebel' },
  { id: 'startrek', labelKey: 'appearanceStarTrek', defaultAppearance: 'st-tng' },
  { id: 'pacificrim', labelKey: 'appearancePacificRim', defaultAppearance: 'pr-gipsy' },
  { id: 'honeycomb', labelKey: 'appearanceHoneycomb', defaultAppearance: 'nav-honeycomb' },
];

export const PR_VARIANT_OPTIONS = [
  { id: 'pr-gipsy', labelKey: 'appearancePrGipsy' },
  { id: 'pr-cherno', labelKey: 'appearancePrCherno' },
  { id: 'pr-kaiju', labelKey: 'appearancePrKaiju' },
  { id: 'pr-shatterdome', labelKey: 'appearancePrShatterdome' },
];

export const SW_VARIANT_OPTIONS = [
  { id: 'sw-rebel', labelKey: 'appearanceSwRebel' },
  { id: 'sw-empire', labelKey: 'appearanceSwEmpire' },
  { id: 'sw-jedi', labelKey: 'appearanceSwJedi' },
  { id: 'sw-sith', labelKey: 'appearanceSwSith' },
  { id: 'sw-mandalorian', labelKey: 'appearanceSwMandalorian' },
];

export const ST_VARIANT_OPTIONS = [
  { id: 'st-tng', labelKey: 'appearanceStTng' },
  { id: 'st-tos', labelKey: 'appearanceStTos' },
  { id: 'st-ds9', labelKey: 'appearanceStDs9' },
  { id: 'st-voyager', labelKey: 'appearanceStVoyager' },
  { id: 'st-discovery', labelKey: 'appearanceStDiscovery' },
];

export const HONEYCOMB_VARIANT_OPTIONS = [
  { id: 'nav-honeycomb', labelKey: 'appearanceHoneycombDark' },
  { id: 'nav-honeycomb-dusk', labelKey: 'appearanceHoneycombDusk' },
  { id: 'nav-honeycomb-light', labelKey: 'appearanceHoneycombLight' },
];

const CURRICULUM_PATTERN =
  "data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d0d0d0' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E";

const PR_COLORS = {
  'pr-gipsy': {
    primary: '#4ad2ff',
    primaryDark: '#2eb8e8',
    secondary: '#8ec8e0',
    bg: '#03080e',
    paper: '#0e1a28',
    text: '#eaf6ff',
    textSecondary: 'rgba(150, 195, 220, 0.72)',
    font: "'Exo 2', 'Segoe UI', sans-serif",
    accentLine: 'rgba(74, 210, 255, 0.12)',
  },
  'pr-cherno': {
    primary: '#e0452f',
    primaryDark: '#c03828',
    secondary: '#8a9a7a',
    bg: '#060806',
    paper: '#1a2218',
    text: '#f0ebe4',
    textSecondary: 'rgba(180, 190, 160, 0.72)',
    font: "'Rajdhani', 'Segoe UI', sans-serif",
    accentLine: 'rgba(224, 69, 47, 0.1)',
  },
  'pr-kaiju': {
    primary: '#b44dff',
    primaryDark: '#9638d9',
    secondary: '#4de8ff',
    bg: '#06030e',
    paper: '#160a24',
    text: '#f2e8ff',
    textSecondary: 'rgba(200, 175, 235, 0.72)',
    font: "'Exo 2', 'Segoe UI', sans-serif",
    accentLine: 'rgba(180, 77, 255, 0.12)',
  },
  'pr-shatterdome': {
    primary: '#e8b800',
    primaryDark: '#c49a00',
    secondary: '#ff3b30',
    bg: '#0a0b0e',
    paper: '#1c2028',
    text: '#eceef2',
    textSecondary: 'rgba(170, 180, 195, 0.75)',
    font: "'Rajdhani', 'Segoe UI', sans-serif",
    accentLine: 'rgba(232, 184, 0, 0.1)',
  },
};

const SW_COLORS = {
  'sw-rebel': {
    primary: '#ffe81f',
    primaryDark: '#d4c010',
    secondary: '#7a8aa3',
    bg: '#010104',
    paper: '#050508',
    text: '#f2f3f7',
    textSecondary: 'rgba(205, 208, 222, 0.68)',
    font: "'Orbitron', 'Segoe UI', sans-serif",
    accentLine: 'rgba(255, 232, 31, 0.18)',
  },
  'sw-empire': {
    primary: '#e11d2e',
    primaryDark: '#b01522',
    secondary: '#8b93a0',
    bg: '#050608',
    paper: '#08090c',
    text: '#f0f1f4',
    textSecondary: 'rgba(200, 205, 215, 0.7)',
    font: "'Orbitron', 'Segoe UI', sans-serif",
    accentLine: 'rgba(225, 29, 46, 0.28)',
  },
  'sw-jedi': {
    primary: '#d4a017',
    primaryDark: '#a87e12',
    secondary: '#c4b59a',
    bg: '#0c0a08',
    paper: '#12100c',
    text: '#f3ebe0',
    textSecondary: 'rgba(220, 205, 180, 0.7)',
    font: "'Orbitron', 'Segoe UI', sans-serif",
    accentLine: 'rgba(212, 160, 23, 0.28)',
  },
  'sw-sith': {
    primary: '#c41e3a',
    primaryDark: '#9a1730',
    secondary: '#7a3a8a',
    bg: '#050206',
    paper: '#0a0408',
    text: '#f5e8ec',
    textSecondary: 'rgba(210, 180, 195, 0.7)',
    font: "'Orbitron', 'Segoe UI', sans-serif",
    accentLine: 'rgba(196, 30, 58, 0.3)',
  },
  'sw-mandalorian': {
    primary: '#c5ccd4',
    primaryDark: '#9aa3ae',
    secondary: '#3d6b4f',
    bg: '#060708',
    paper: '#0a0c0e',
    text: '#e8ecef',
    textSecondary: 'rgba(180, 190, 200, 0.72)',
    font: "'Orbitron', 'Segoe UI', sans-serif",
    accentLine: 'rgba(197, 204, 212, 0.28)',
  },
};

const ST_COLORS = {
  'st-tng': {
    primary: '#f2a13c',
    primaryDark: '#d4892a',
    secondary: '#aa88cc',
    bg: '#000000',
    paper: '#0a0a0a',
    text: '#ffffff',
    textSecondary: 'rgba(200, 200, 210, 0.72)',
    font: "'Antonio', 'Arial Narrow', sans-serif",
    accentLine: 'rgba(242, 161, 60, 0.28)',
  },
  'st-tos': {
    primary: '#3ecfcf',
    primaryDark: '#2aa8a8',
    secondary: '#7ec8e3',
    bg: '#000000',
    paper: '#0a0a0a',
    text: '#ffffff',
    textSecondary: 'rgba(190, 210, 215, 0.72)',
    font: "'Antonio', 'Arial Narrow', sans-serif",
    accentLine: 'rgba(62, 207, 207, 0.28)',
  },
  'st-ds9': {
    primary: '#d4893a',
    primaryDark: '#b07028',
    secondary: '#c4a882',
    bg: '#000000',
    paper: '#0a0a0a',
    text: '#ffffff',
    textSecondary: 'rgba(210, 200, 185, 0.72)',
    font: "'Antonio', 'Arial Narrow', sans-serif",
    accentLine: 'rgba(212, 137, 58, 0.28)',
  },
  'st-voyager': {
    primary: '#4a9fff',
    primaryDark: '#3d7ecc',
    secondary: '#7b8cff',
    bg: '#000000',
    paper: '#0a0a0a',
    text: '#ffffff',
    textSecondary: 'rgba(185, 200, 230, 0.72)',
    font: "'Antonio', 'Arial Narrow', sans-serif",
    accentLine: 'rgba(74, 159, 255, 0.28)',
  },
  'st-discovery': {
    primary: '#e8c547',
    primaryDark: '#c4a838',
    secondary: '#2ec4ff',
    bg: '#000000',
    paper: '#0a0a0a',
    text: '#ffffff',
    textSecondary: 'rgba(210, 205, 180, 0.72)',
    font: "'Antonio', 'Arial Narrow', sans-serif",
    accentLine: 'rgba(232, 197, 71, 0.28)',
  },
};

/** Navigator-Wabenraster (SVG-Stroke-Kachel). */
const HONEYCOMB_HEX_PATTERN_URL =
  "url(\"data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2272%22%20height%3D%2262.36%22%20viewBox%3D%220%200%2072%2062.36%22%3E%3Cg%20fill%3D%22none%22%20stroke%3D%22%239aa3af%22%20stroke-opacity%3D%220.12%22%20stroke-width%3D%221.25%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22M18.00%200.00%20L36.00%2010.39%20L36.00%2031.18%20L18.00%2041.57%20L0.00%2031.18%20L0.00%2010.39%20Z%22%2F%3E%3Cpath%20d%3D%22M54.00%200.00%20L72.00%2010.39%20L72.00%2031.18%20L54.00%2041.57%20L36.00%2031.18%20L36.00%2010.39%20Z%22%2F%3E%3Cpath%20d%3D%22M36.00%2031.18%20L54.00%2041.57%20L54.00%2062.36%20L36.00%2072.75%20L18.00%2062.36%20L18.00%2041.57%20Z%22%2F%3E%3Cpath%20d%3D%22M-18.00%200.00%20L0.00%2010.39%20L0.00%2031.18%20L-18.00%2041.57%20L-36.00%2031.18%20L-36.00%2010.39%20Z%22%2F%3E%3Cpath%20d%3D%22M90.00%200.00%20L108.00%2010.39%20L108.00%2031.18%20L90.00%2041.57%20L72.00%2031.18%20L72.00%2010.39%20Z%22%2F%3E%3Cpath%20d%3D%22M0.00%2031.18%20L18.00%2041.57%20L18.00%2062.36%20L0.00%2072.75%20L-18.00%2062.36%20L-18.00%2041.57%20Z%22%2F%3E%3Cpath%20d%3D%22M72.00%2031.18%20L90.00%2041.57%20L90.00%2062.36%20L72.00%2072.75%20L54.00%2062.36%20L54.00%2041.57%20Z%22%2F%3E%3Cpath%20d%3D%22M36.00%20-31.18%20L54.00%20-20.79%20L54.00%20-0.00%20L36.00%2010.39%20L18.00%20-0.00%20L18.00%20-20.79%20Z%22%2F%3E%3Cpath%20d%3D%22M0.00%20-31.18%20L18.00%20-20.79%20L18.00%20-0.00%20L0.00%2010.39%20L-18.00%20-0.00%20L-18.00%20-20.79%20Z%22%2F%3E%3Cpath%20d%3D%22M72.00%20-31.18%20L90.00%20-20.79%20L90.00%20-0.00%20L72.00%2010.39%20L54.00%20-0.00%20L54.00%20-20.79%20Z%22%2F%3E%3Cpath%20d%3D%22M18.00%2062.36%20L36.00%2072.75%20L36.00%2093.54%20L18.00%20103.93%20L0.00%2093.54%20L0.00%2072.75%20Z%22%2F%3E%3Cpath%20d%3D%22M54.00%2062.36%20L72.00%2072.75%20L72.00%2093.54%20L54.00%20103.93%20L36.00%2093.54%20L36.00%2072.75%20Z%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E\")";

const HONEYCOMB_COLORS = {
  'nav-honeycomb': {
    primary: '#9a9a9e',
    primaryDark: '#6e6e72',
    secondary: '#c8c8cc',
    bg: '#121314',
    paper: '#2a2a2a',
    text: '#e8e8ea',
    textSecondary: 'rgba(200, 200, 205, 0.72)',
    font: "'Nunito', 'Segoe UI', sans-serif",
    accentLine: 'rgba(255, 255, 255, 0.12)',
  },
  'nav-honeycomb-dusk': {
    primary: '#b4b4ba',
    primaryDark: '#8a8a90',
    secondary: '#d4d4d8',
    bg: '#1a1e24',
    paper: '#222830',
    text: '#f0f0f2',
    textSecondary: 'rgba(220, 220, 225, 0.78)',
    font: "'Nunito', 'Segoe UI', sans-serif",
    accentLine: 'rgba(138, 160, 180, 0.22)',
  },
};

const HONEYCOMB_LIGHT_COLORS = {
  primary: '#52525a',
  primaryDark: '#3f3f46',
  secondary: '#71717a',
  bg: '#ececef',
  paper: '#ffffff',
  text: '#18181b',
  textSecondary: 'rgba(24, 24, 27, 0.62)',
  font: "'Nunito', 'Segoe UI', sans-serif",
  accentLine: 'rgba(0, 0, 0, 0.1)',
};

export function isHoneycombAppearance(id) {
  return String(id || '').startsWith('nav-honeycomb');
}

export function isHoneycombLightAppearance(id) {
  return id === 'nav-honeycomb-light';
}

export function isHoneycombDuskAppearance(id) {
  return id === 'nav-honeycomb-dusk';
}

/** Amber für Löschen/X-Buttons im Waben-Design (normal). */
export const HONEYCOMB_CLEAR_AMBER = '#f0b429';
export const HONEYCOMB_CLEAR_AMBER_DARK = '#c99212';

/** @returns {import('@mui/material').SxProps} */
export function honeycombClearIconButtonSx() {
  return {
    bgcolor: 'var(--honey-ring, #f0b429)',
    color: '#140e06',
    '&:hover': { bgcolor: 'color-mix(in srgb, var(--honey-ring, #f0b429) 82%, #000)' },
    '&:disabled': {
      bgcolor: 'action.disabledBackground',
      color: 'action.disabled',
    },
  };
}

const HONEYCOMB_CELL_WIDTH_PX = 107;
const HONEYCOMB_CELL_HEIGHT_PX = 93;
const HONEYCOMB_BG_HEX_WIDTH_PX = 124;
const HONEYCOMB_BG_HEX_HEIGHT_PX = 106;
const HONEYCOMB_LINE_PX = 22.68;
const HONEYCOMB_COL_STEP_PX = 205.35;
const HONEYCOMB_ROW_STEP_PX = 57.66;
const HONEYCOMB_PATTERN_TILE_W = 410.7;
const HONEYCOMB_PATTERN_TILE_H = 115.32;

function clampFillLineScale(value) {
  const stepped = Math.round(value / 0.05) * 0.05;
  return Math.min(2, Math.max(0.05, Math.round(stepped * 100) / 100));
}

function expandedHexVerts(ox, oy, w, h, line) {
  const outerH = h + line;
  const outerW = (w * outerH) / h;
  const oxo = ox - (outerW - w) / 2;
  const oyo = oy - line / 2;
  return [
    [oxo + outerW / 4, oyo],
    [oxo + (3 * outerW) / 4, oyo],
    [oxo + outerW, oyo + outerH / 2],
    [oxo + (3 * outerW) / 4, oyo + outerH],
    [oxo + outerW / 4, oyo + outerH],
    [oxo, oyo + outerH / 2],
  ];
}

function signedEdgeGap(a1, a2, b1) {
  const nx = a2[1] - a1[1];
  const ny = a1[0] - a2[0];
  const len = Math.hypot(nx, ny) || 1;
  return ((b1[0] - a1[0]) * nx + (b1[1] - a1[1]) * ny) / len;
}

function honeycombSkeletonExpandPx(w, h, col, row) {
  const gapAt = (line) => {
    const A = expandedHexVerts(0, 0, w, h, line);
    const B = expandedHexVerts(col / 2, row, w, h, line);
    return signedEdgeGap(A[2], A[3], B[5]);
  };
  let lo = 0;
  let hi = Math.max(h, w);
  const g0 = gapAt(0);
  if (!Number.isFinite(g0) || Math.abs(g0) < 0.05) return 0;
  for (let i = 0; i < 32; i++) {
    const mid = (lo + hi) / 2;
    const g = gapAt(mid);
    if (g0 > 0 ? g > 0 : g < 0) lo = mid;
    else hi = mid;
  }
  return Math.round(((lo + hi) / 2) * 100) / 100;
}

function uniqueSkeletonEdgePaths(origins, originShiftX, originShiftY, w, h, expand) {
  const weld = [];
  const weldOf = (x, y) => {
    for (const p of weld) {
      if (Math.hypot(p[0] - x, p[1] - y) < 1.25) return p;
    }
    const p = [x, y];
    weld.push(p);
    return p;
  };
  const edges = new Map();
  for (const [ox, oy] of origins) {
    const verts = expandedHexVerts(ox + originShiftX, oy + originShiftY, w, h, expand).map(([x, y]) =>
      weldOf(x, y)
    );
    for (let i = 0; i < 6; i++) {
      const a = verts[i];
      const b = verts[(i + 1) % 6];
      if (a === b) continue;
      const key =
        a[0] < b[0] || (a[0] === b[0] && a[1] < b[1])
          ? `${a[0]},${a[1]}|${b[0]},${b[1]}`
          : `${b[0]},${b[1]}|${a[0]},${a[1]}`;
      if (!edges.has(key)) edges.set(key, [a[0], a[1], b[0], b[1]]);
    }
  }
  return [...edges.values()];
}

function defaultHoneycombBoardFill(id) {
  if (isHoneycombLightAppearance(id)) return '#f2f2f5';
  if (isHoneycombDuskAppearance(id)) return '#222830';
  return '#151617';
}

function defaultHoneycombBoardSurface(id) {
  if (isHoneycombLightAppearance(id)) return '#ececef';
  if (isHoneycombDuskAppearance(id)) return '#1a1e24';
  return '#121314';
}

export function resolvedHoneycombBoardSurface(id, maps) {
  return maps?.surface?.[id] || defaultHoneycombBoardSurface(id);
}

export function honeycombSurfaceWashCss(hex, showThrough) {
  const n =
    hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex;
  if (!/^#[0-9a-fA-F]{6}$/.test(n)) return hex;
  const rgb = `${parseInt(n.slice(1, 3), 16)}, ${parseInt(n.slice(3, 5), 16)}, ${parseInt(n.slice(5, 7), 16)}`;
  const amount = Math.min(0.45, Math.max(0.06, showThrough));
  const alpha = Math.round((1 - amount) * 1000) / 1000;
  return `rgba(${rgb}, ${alpha})`;
}

function honeycombPatternDataUrl(color, fillLineScale = 1) {
  const w = HONEYCOMB_BG_HEX_WIDTH_PX;
  const h = HONEYCOMB_BG_HEX_HEIGHT_PX;
  const originShiftX = (HONEYCOMB_CELL_WIDTH_PX - w) / 2;
  const originShiftY = (HONEYCOMB_CELL_HEIGHT_PX - h) / 2;
  const col = HONEYCOMB_COL_STEP_PX;
  const row = HONEYCOMB_ROW_STEP_PX;
  const line = Math.max(
    0.35,
    Math.round(HONEYCOMB_LINE_PX * clampFillLineScale(fillLineScale > 0 ? fillLineScale : 1) * 100) / 100
  );
  const skeletonExpand = honeycombSkeletonExpandPx(w, h, col, row);
  const origins = [
    [0, 0], [col, 0], [-col, 0], [2 * col, 0],
    [col / 2, row], [-col / 2, row], [(3 * col) / 2, row], [(5 * col) / 2, row],
    [col / 2, -row], [-col / 2, -row], [(3 * col) / 2, -row],
    [0, 2 * row], [col, 2 * row], [-col, 2 * row], [2 * col, 2 * row],
  ];
  const paths = uniqueSkeletonEdgePaths(
    origins,
    originShiftX,
    originShiftY,
    w,
    h,
    skeletonExpand
  )
    .map(([x1, y1, x2, y2]) =>
      `<path d="M${x1.toFixed(2)} ${y1.toFixed(2)} L${x2.toFixed(2)} ${y2.toFixed(2)}" fill="none" stroke="${color}" stroke-width="${line}" stroke-linejoin="round" stroke-linecap="round"/>`
    )
    .join('');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${HONEYCOMB_PATTERN_TILE_W}" height="${HONEYCOMB_PATTERN_TILE_H}" viewBox="0 0 ${HONEYCOMB_PATTERN_TILE_W} ${HONEYCOMB_PATTERN_TILE_H}">${paths}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

function honeycombHexPatternUrl(id, maps) {
  const fill = maps?.accent?.[id] || defaultHoneycombBoardFill(id);
  return honeycombPatternDataUrl(fill, maps?.fillLineScale);
}

function honeycombPatternLayerSx(patternUrl) {
  const fadeMask = [
    'linear-gradient(to right, transparent 0%, #000 min(22%, 168px), #000 calc(100% - min(22%, 168px)), transparent 100%)',
    'linear-gradient(to bottom, transparent 0%, #000 min(22%, 168px), #000 calc(100% - min(22%, 168px)), transparent 100%)',
  ].join(', ');
  return {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: -1,
    pointerEvents: 'none',
    backgroundImage: patternUrl,
    backgroundSize: `${HONEYCOMB_PATTERN_TILE_W}px ${HONEYCOMB_PATTERN_TILE_H}px`,
    backgroundRepeat: 'repeat',
    WebkitMaskImage: fadeMask,
    WebkitMaskSize: '100% 100%, 100% 100%',
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskComposite: 'destination-in',
    maskImage: fadeMask,
    maskSize: '100% 100%, 100% 100%',
    maskRepeat: 'no-repeat',
    maskComposite: 'intersect',
    animation: 'honeycombHexPulse 6s ease-in-out infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      opacity: 1,
    },
  };
}

function honeycombBackdropImage(surface, kind) {
  if (kind === 'light') {
    return `
      radial-gradient(ellipse 80% 45% at 50% -5%, rgba(255, 255, 255, 0.9), transparent 55%),
      radial-gradient(ellipse 50% 40% at 80% 100%, rgba(0, 0, 0, 0.04), transparent 50%),
      linear-gradient(180deg, color-mix(in srgb, ${surface} 70%, #fff) 0%, ${surface} 55%, color-mix(in srgb, ${surface} 86%, #000) 100%)
    `;
  }
  if (kind === 'dusk') {
    return `
      radial-gradient(ellipse 80% 45% at 50% -5%, rgba(138, 160, 180, 0.1), transparent 55%),
      radial-gradient(ellipse 50% 40% at 80% 100%, rgba(0, 0, 0, 0.28), transparent 50%),
      linear-gradient(180deg, color-mix(in srgb, ${surface} 78%, #fff) 0%, ${surface} 55%, color-mix(in srgb, ${surface} 82%, #000) 100%)
    `;
  }
  return `
    radial-gradient(ellipse 80% 45% at 50% -5%, rgba(255, 255, 255, 0.04), transparent 55%),
    radial-gradient(ellipse 50% 40% at 80% 100%, rgba(0, 0, 0, 0.35), transparent 50%),
    linear-gradient(180deg, color-mix(in srgb, ${surface} 82%, #fff) 0%, ${surface} 55%, color-mix(in srgb, ${surface} 70%, #000) 100%)
  `;
}

function withHoneycombSurface(id, maps, sx) {
  const surface = maps?.surface?.[id] || defaultHoneycombBoardSurface(id);
  return {
    ...sx,
    backgroundColor: surface,
    backgroundImage: honeycombBackdropImage(
      surface,
      isHoneycombLightAppearance(id) ? 'light' : isHoneycombDuskAppearance(id) ? 'dusk' : 'dark'
    ),
    position: sx.position ?? 'relative',
    isolation: 'isolate',
    '@keyframes honeycombHexPulse': {
      '0%, 100%': { opacity: 0.55 },
      '50%': { opacity: 1 },
    },
    '&::before': honeycombPatternLayerSx(honeycombHexPatternUrl(id, maps)),
  };
}

const ALL_DARK_COLORS = {
  ...PR_COLORS,
  ...SW_COLORS,
  ...ST_COLORS,
  ...HONEYCOMB_COLORS,
};

const TOUCH_COMPONENTS = {
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: 12,
        minHeight: 44,
        '@media (min-width: 600px)': { minHeight: 36 },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        minWidth: 44,
        minHeight: 44,
        '@media (min-width: 600px)': { minWidth: 40, minHeight: 40 },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        minHeight: 48,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'none' },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiInputBase-input': {
          '@media (max-width: 767px)': { fontSize: '16px' },
        },
        '& .MuiInputLabel-root': {
          '@media (max-width: 767px)': { zIndex: 1 },
          '&.MuiInputLabel-shrink': {
            backgroundColor: 'var(--mui-palette-background-paper, #fff)',
            paddingLeft: '4px',
            paddingRight: '4px',
            marginLeft: '-4px',
            marginRight: '-4px',
          },
        },
      },
    },
  },
};

export function appearanceFamilyOf(id) {
  const s = String(id);
  if (s.startsWith('nav-honeycomb')) return 'honeycomb';
  if (s.startsWith('pr-')) return 'pacificrim';
  if (s.startsWith('sw-')) return 'starwars';
  if (s.startsWith('st-')) return 'startrek';
  return 'curriculum';
}

export function isAppAppearanceId(raw) {
  if (!raw) return false;
  if (raw === 'curriculum' || raw === 'nav-honeycomb-light') {
    return true;
  }
  return Boolean(ALL_DARK_COLORS[raw]);
}

/** Alte Falcon-IDs und Navigator-Kürzel → aktuelles Waben-Design. */
export function normalizeAppAppearanceId(raw) {
  if (raw === 'nav-honeycomb-falcon' || raw === 'honeycomb') return 'nav-honeycomb';
  if (raw === 'nav-honeycomb-falcon-dusk') return 'nav-honeycomb-dusk';
  if (raw === 'nav-honeycomb-falcon-light') return 'nav-honeycomb-light';
  if (raw === 'nav-honeycomb-slate') return 'nav-honeycomb-dusk';
  return isAppAppearanceId(raw) ? raw : null;
}

export function readAppAppearance() {
  try {
    const raw = localStorage.getItem(APP_APPEARANCE_STORAGE_KEY);
    return normalizeAppAppearanceId(raw) ?? 'curriculum';
  } catch {
    return 'curriculum';
  }
}

export function writeAppAppearance(id) {
  try {
    if (id === 'curriculum') localStorage.removeItem(APP_APPEARANCE_STORAGE_KEY);
    else localStorage.setItem(APP_APPEARANCE_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

function createCurriculumTheme() {
  return createTheme({
    palette: {
      mode: 'light',
      primary: { main: '#667eea', dark: '#5a6fd8', light: '#8b9cf0' },
      secondary: { main: '#764ba2', dark: '#6a4190', light: '#9a67b5' },
      background: { default: '#e8e8e8', paper: '#ffffff' },
      text: { primary: 'rgba(0, 0, 0, 0.87)', secondary: '#757575' },
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'sans-serif',
      ].join(','),
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      ...TOUCH_COMPONENTS,
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-input': {
              '@media (max-width: 767px)': { fontSize: '16px' },
            },
            '& .MuiInputLabel-root': {
              '@media (max-width: 767px)': { zIndex: 1 },
              '&.MuiInputLabel-shrink': {
                backgroundColor: '#ffffff',
                paddingLeft: '4px',
                paddingRight: '4px',
                marginLeft: '-4px',
                marginRight: '-4px',
              },
            },
          },
        },
      },
    },
  });
}

function createDarkAppearanceTheme(colors) {
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: colors.primary,
        dark: colors.primaryDark,
        light: colors.secondary,
      },
      secondary: {
        main: colors.secondary,
        dark: colors.primaryDark,
        light: colors.primary,
      },
      background: { default: colors.bg, paper: colors.paper },
      text: { primary: colors.text, secondary: colors.textSecondary },
      divider: colors.accentLine,
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: colors.font,
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      ...TOUCH_COMPONENTS,
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-input': {
              '@media (max-width: 767px)': { fontSize: '16px' },
            },
            '& .MuiInputLabel-root': {
              '@media (max-width: 767px)': { zIndex: 1 },
              '&.MuiInputLabel-shrink': {
                backgroundColor: colors.paper,
                paddingLeft: '4px',
                paddingRight: '4px',
                marginLeft: '-4px',
                marginRight: '-4px',
              },
            },
          },
        },
      },
    },
  });
}

function createLightAppearanceTheme(colors) {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: colors.primary,
        dark: colors.primaryDark,
        light: colors.secondary,
      },
      secondary: {
        main: colors.secondary,
        dark: colors.primaryDark,
        light: colors.primary,
      },
      background: { default: colors.bg, paper: colors.paper },
      text: { primary: colors.text, secondary: colors.textSecondary },
      divider: colors.accentLine,
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: colors.font,
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: TOUCH_COMPONENTS,
  });
}

export function createThemeForAppearance(id, colorMaps) {
  if (id === 'curriculum') return createCurriculumTheme();
  if (id === 'nav-honeycomb-light') {
    return createLightAppearanceTheme(
      applyHoneycombColorOverrides(id, HONEYCOMB_LIGHT_COLORS, colorMaps)
    );
  }
  const colors = ALL_DARK_COLORS[id];
  if (!colors) return createCurriculumTheme();
  return createDarkAppearanceTheme(
    applyHoneycombColorOverrides(id, colors, colorMaps)
  );
}

function applyHoneycombColorOverrides(id, colors, maps) {
  if (!maps || !String(id || '').startsWith('nav-honeycomb')) return colors;
  const next = { ...colors };
  if (maps.surface?.[id]) next.bg = maps.surface[id];
  if (maps.stroke?.[id]) {
    next.primary = maps.stroke[id];
    next.primaryDark = maps.stroke[id];
  } else if (maps.ring?.[id]) {
    next.primary = maps.ring[id];
    next.primaryDark = maps.ring[id];
  }
  if (maps.ring?.[id]) next.secondary = maps.ring[id];
  return next;
}

const SW_STARFIELD_IMAGE = `
  radial-gradient(1px 1px at 22px 34px, rgba(255, 255, 255, 0.9), transparent 100%),
  radial-gradient(1px 1px at 118px 76px, rgba(255, 255, 255, 0.55), transparent 100%),
  radial-gradient(1.6px 1.6px at 71px 141px, rgba(255, 255, 255, 0.95), transparent 100%),
  radial-gradient(1px 1px at 186px 27px, rgba(255, 255, 255, 0.5), transparent 100%),
  radial-gradient(1.3px 1.3px at 157px 183px, rgba(255, 255, 255, 0.75), transparent 100%),
  radial-gradient(1px 1px at 42px 199px, rgba(255, 255, 255, 0.6), transparent 100%),
  radial-gradient(1.5px 1.5px at 214px 118px, rgba(200, 220, 255, 0.85), transparent 100%)
`;

const SW_STARFIELD_SIZE = `
  231px 231px, 247px 247px, 283px 283px, 311px 311px,
  353px 353px, 397px 397px, 431px 431px
`;

export function backgroundSxForAppearance(id, colorMaps) {
  if (id === 'curriculum') {
    return {
      backgroundColor: '#e8e8e8',
      backgroundImage: `url("${CURRICULUM_PATTERN}")`,
    };
  }

  if (String(id).startsWith('sw-')) {
    return {
      backgroundColor: SW_COLORS[id].bg,
      backgroundImage: SW_STARFIELD_IMAGE,
      backgroundSize: SW_STARFIELD_SIZE,
    };
  }

  if (String(id).startsWith('st-')) {
    return { backgroundColor: '#000000', backgroundImage: 'none' };
  }

  if (id === 'pr-gipsy') {
    /* Wie Navigator `.pr-gipsy .hero.fullscreen::before` + Conn-Pod-Backdrop */
    return {
      backgroundColor: '#03080e',
      backgroundImage: `
        radial-gradient(
          circle at 50% 48%,
          transparent 28%,
          rgba(74, 210, 255, 0.04) 42%,
          transparent 58%
        ),
        repeating-linear-gradient(
          60deg,
          transparent 0 14px,
          rgba(74, 210, 255, 0.05) 14px 15px
        ),
        repeating-linear-gradient(
          -60deg,
          transparent 0 14px,
          rgba(74, 210, 255, 0.04) 14px 15px
        ),
        repeating-linear-gradient(
          0deg,
          transparent 0 14px,
          rgba(74, 210, 255, 0.035) 14px 15px
        ),
        radial-gradient(ellipse 80% 45% at 50% -5%, rgba(74, 210, 255, 0.18), transparent 55%),
        radial-gradient(ellipse 50% 40% at 80% 100%, rgba(100, 140, 170, 0.1), transparent 50%),
        linear-gradient(180deg, #0a1824 0%, #03080e 55%, #02060a 100%)
      `,
    };
  }

  if (id === 'pr-cherno') {
    return {
      backgroundColor: '#060806',
      backgroundImage: `
        repeating-linear-gradient(0deg, transparent 0 15px, rgba(90, 110, 70, 0.07) 15px 16px),
        repeating-linear-gradient(90deg, transparent 0 15px, rgba(224, 69, 47, 0.04) 15px 16px),
        radial-gradient(ellipse 70% 40% at 50% -5%, rgba(224, 69, 47, 0.16), transparent 55%),
        radial-gradient(ellipse 55% 45% at 15% 100%, rgba(60, 80, 40, 0.22), transparent 55%),
        linear-gradient(180deg, #141a12 0%, #060806 55%, #040504 100%)
      `,
    };
  }

  if (id === 'pr-kaiju') {
    return {
      backgroundColor: '#06030e',
      backgroundImage: `
        repeating-radial-gradient(circle at 50% 42%, transparent 0 26px, rgba(180, 77, 255, 0.07) 26px 28px),
        repeating-radial-gradient(circle at 50% 42%, transparent 0 52px, rgba(77, 232, 255, 0.05) 52px 54px),
        radial-gradient(ellipse 70% 50% at 50% 20%, rgba(180, 77, 255, 0.22), transparent 55%),
        radial-gradient(ellipse 45% 40% at 50% 78%, rgba(77, 232, 255, 0.12), transparent 55%),
        linear-gradient(180deg, #12081c 0%, #06030e 55%, #04020a 100%)
      `,
    };
  }

  if (id === 'nav-honeycomb') {
    return withHoneycombSurface(id, colorMaps, {
      backgroundColor: '#121314',
      backgroundImage: `
        radial-gradient(ellipse 80% 45% at 50% -5%, rgba(255, 255, 255, 0.04), transparent 55%),
        radial-gradient(ellipse 50% 40% at 80% 100%, rgba(0, 0, 0, 0.35), transparent 50%),
        linear-gradient(180deg, #1c1d1f 0%, #121314 55%, #0c0d0e 100%)
      `,
    });
  }

  if (id === 'nav-honeycomb-dusk') {
    return withHoneycombSurface(id, colorMaps, {
      backgroundColor: '#1a1e24',
      backgroundImage: `
        radial-gradient(ellipse 80% 45% at 50% -5%, rgba(138, 160, 180, 0.1), transparent 55%),
        radial-gradient(ellipse 50% 40% at 80% 100%, rgba(0, 0, 0, 0.28), transparent 50%),
        linear-gradient(180deg, #222830 0%, #1a1e24 55%, #14181d 100%)
      `,
    });
  }

  if (id === 'nav-honeycomb-light') {
    return withHoneycombSurface(id, colorMaps, {
      backgroundColor: '#ececef',
      backgroundImage: `
        radial-gradient(ellipse 80% 45% at 50% -5%, rgba(255, 255, 255, 0.9), transparent 55%),
        radial-gradient(ellipse 50% 40% at 80% 100%, rgba(0, 0, 0, 0.04), transparent 50%),
        linear-gradient(180deg, #f4f4f6 0%, #ececef 55%, #e4e4e8 100%)
      `,
    });
  }

  return {
    backgroundColor: '#0a0b0e',
    backgroundImage: `
      repeating-linear-gradient(90deg, transparent 0 47px, rgba(255, 255, 255, 0.035) 47px 50px, transparent 50px 98px),
      repeating-linear-gradient(0deg, transparent 0 47px, rgba(0, 0, 0, 0.18) 47px 48px),
      linear-gradient(180deg, #1c1e24 0%, #0a0b0e 40%, #07080a 100%)
    `,
    backgroundSize: '98px 100%, 100% 48px, 100% 100%',
    backgroundRepeat: 'repeat, repeat, no-repeat',
  };
}

/** Inset des HUD-Rahmens in px (0 wenn kein Rahmen). */
export function prViewportFrameInset(id) {
  if (id === 'pr-gipsy') return 16;
  if (id === 'pr-cherno') return 20;
  if (id === 'pr-kaiju') return 20;
  if (id === 'pr-shatterdome') return 18;
  return 0;
}

/** HUD-Rahmen für Pacific-Rim-Designs — schmaler als das Hintergrundraster. */
export function prViewportFrameSx(id) {
  const inset = prViewportFrameInset(id);
  if (!inset) return null;

  if (id === 'pr-gipsy') {
    // Raster ~12px → Ecken kürzer/dünner als eine Musterzelle
    const cyan = '#4ad2ff';
    const ice = '#b8f4ff';
    return {
      position: 'absolute',
      inset: `${inset}px`,
      pointerEvents: 'none',
      zIndex: 20,
      opacity: 0.85,
      border: 'none',
      borderRadius: 0,
      background: [
        `linear-gradient(${cyan}, ${cyan}) top left / 10px 1px no-repeat`,
        `linear-gradient(${cyan}, ${cyan}) top left / 1px 10px no-repeat`,
        `linear-gradient(${ice}, ${ice}) top right / 10px 1px no-repeat`,
        `linear-gradient(${ice}, ${ice}) top right / 1px 10px no-repeat`,
        `linear-gradient(${ice}, ${ice}) bottom left / 10px 1px no-repeat`,
        `linear-gradient(${ice}, ${ice}) bottom left / 1px 10px no-repeat`,
        `linear-gradient(${cyan}, ${cyan}) bottom right / 10px 1px no-repeat`,
        `linear-gradient(${cyan}, ${cyan}) bottom right / 1px 10px no-repeat`,
      ].join(', '),
      boxShadow:
        'inset 0 0 28px rgba(74, 210, 255, 0.05), 0 0 16px rgba(74, 210, 255, 0.06)',
      filter: 'drop-shadow(0 0 3px rgba(74, 210, 255, 0.35))',
    };
  }

  if (id === 'pr-cherno') {
    return {
      position: 'absolute',
      inset: `${inset}px`,
      pointerEvents: 'none',
      zIndex: 20,
      opacity: 0.8,
      border: '1px solid color-mix(in srgb, #e0452f 40%, #3a4a30)',
      boxShadow:
        'inset 0 0 36px rgba(224, 69, 47, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.4)',
      clipPath: `polygon(
        0 6px, 6px 0, calc(100% - 6px) 0, 100% 6px,
        100% calc(100% - 6px), calc(100% - 6px) 100%,
        6px 100%, 0 calc(100% - 6px)
      )`,
    };
  }

  if (id === 'pr-kaiju') {
    return {
      position: 'absolute',
      inset: `${inset}px`,
      pointerEvents: 'none',
      zIndex: 20,
      opacity: 0.75,
      border: '1px solid color-mix(in srgb, #4de8ff 35%, transparent)',
      borderRadius: '42% 58% 48% 52% / 48% 42% 58% 52%',
      boxShadow:
        'inset 0 0 28px rgba(180, 77, 255, 0.08), 0 0 18px rgba(77, 232, 255, 0.06)',
    };
  }

  if (id === 'pr-shatterdome') {
    return {
      position: 'absolute',
      inset: `${inset}px`,
      pointerEvents: 'none',
      zIndex: 20,
      opacity: 0.85,
      border: '1px solid #2a2e38',
      boxShadow:
        'inset 0 0 0 1px rgba(232, 184, 0, 0.14), 0 0 0 1px #12141a',
    };
  }

  return null;
}
